import React from 'react';
import styles from "./Paginator.module.css";
import cn from "classnames";

const range = (from, to) => Array.from({length: to - from + 1}, (_, i) => from + i);

/**
 * Номера страниц вокруг текущей: всегда первая и последняя, рядом с текущей —
 * по `siblings` с каждой стороны, пропуски обозначаются многоточием.
 * Например, для 128 страниц и текущей 6: 1 … 4 5 6 7 8 … 128
 *
 * Число позиций всегда одинаковое (2 * siblings + 5), а у краёв окно номеров
 * расширяется вместо многоточия: так пагинатор не меняет ширину при переходах
 * и кнопка «Вперёд» не уезжает из-под курсора.
 */
export const getPageItems = (currentPage, pagesCount, siblings = 2) => {
    if (pagesCount <= 1) return [];

    const slots = 2 * siblings + 5;
    if (pagesCount <= slots) return range(1, pagesCount);

    const edge = siblings + 3; // сколько номеров подряд показывать у края
    if (currentPage <= edge) {
        return [...range(1, slots - 2), 'gap-right', pagesCount];
    }
    if (currentPage > pagesCount - edge) {
        return [1, 'gap-left', ...range(pagesCount - slots + 3, pagesCount)];
    }
    return [1, 'gap-left', ...range(currentPage - siblings, currentPage + siblings), 'gap-right', pagesCount];
};

const Paginator = ({totalItemsCount, pageSize, currentPage, onPageChanged, className}) => {
    const pagesCount = Math.ceil(totalItemsCount / pageSize);

    // Одна страница — переключать нечего.
    if (!pagesCount || pagesCount <= 1) return null;

    const page = Math.min(Math.max(1, currentPage), pagesCount);
    // На телефоне — по одному соседнему номеру, чтобы пагинатор помещался в одну строку.
    const siblings = typeof window !== 'undefined' && window.innerWidth < 576 ? 1 : 2;
    const go = (p) => {
        if (p !== page && p >= 1 && p <= pagesCount) onPageChanged(p);
    };

    return (
        <nav className={cn(styles.paginator, className)} aria-label="Страницы">
            <button
                type="button"
                className={cn(styles.page, styles.arrow)}
                onClick={() => go(page - 1)}
                disabled={page === 1}
                aria-label="Предыдущая страница"
            >
                ‹<span className={styles.arrowText}> Назад</span>
            </button>

            {getPageItems(page, pagesCount, siblings).map((item) =>
                typeof item === 'number' ? (
                    <button
                        type="button"
                        key={item}
                        className={cn(styles.page, {[styles.selected]: item === page})}
                        onClick={() => go(item)}
                        aria-current={item === page ? 'page' : undefined}
                        aria-label={`Страница ${item}`}
                    >
                        {item}
                    </button>
                ) : (
                    <span key={item} className={styles.gap} aria-hidden="true">…</span>
                )
            )}

            <button
                type="button"
                className={cn(styles.page, styles.arrow)}
                onClick={() => go(page + 1)}
                disabled={page === pagesCount}
                aria-label="Следующая страница"
            >
                <span className={styles.arrowText}>Вперёд </span>›
            </button>
        </nav>
    );
};

export default Paginator;
